import React, { useState, useEffect, useContext } from 'react';
import { Typography, Input, Button, Space, Alert, Card, message } from 'antd';
import { SaveOutlined, RobotOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

const { TextArea } = Input;

const HINT = `This is the personality and focus instruction that shapes how the AI responds.
The database schema (table names, columns, SQL rules) is always included automatically — you don't need to add it here.

Examples of what to put here:
  • The company or team name, so the AI can reference it naturally
  • Response style preferences (concise vs. detailed, formal vs. casual)
  • Domain context ("agents handle insurance calls for residential customers")
  • Things to emphasize ("always flag agents scoring below 70%")
  • Things to avoid ("don't speculate — if data isn't in the DB, say so")`;

export default function SidekickConfigPage() {
  const currentUser = useContext(UserContext);
  const canEdit     = currentUser?.Role === 'admin';
  const [prompt, setPrompt]   = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    api.get('/api/sidekick/config')
      .then((r) => { setPrompt(r.data.prompt); setDirty(false); })
      .catch(() => message.error('Failed to load config.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    setPrompt(e.target.value);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/sidekick/config', { prompt });
      message.success('System prompt saved.');
      setDirty(false);
    } catch {
      message.error('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <RobotOutlined style={{ marginRight: 8, color: '#1677ff' }} />
          AI Sidekick — System Prompt
        </Typography.Title>
        {canEdit && (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!dirty || loading}
            onClick={handleSave}
          >
            Save
          </Button>
        )}
      </div>

      <Alert
        type="info"
        message="What goes here"
        description={<pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: 13 }}>{HINT}</pre>}
        style={{ marginBottom: 20 }}
      />

      <Card>
        <TextArea
          value={prompt}
          onChange={canEdit ? handleChange : undefined}
          readOnly={!canEdit}
          disabled={loading}
          autoSize={{ minRows: 14, maxRows: 40 }}
          style={{ fontFamily: 'monospace', fontSize: 13 }}
          placeholder="Enter your system prompt instructions here…"
        />
        <Space style={{ marginTop: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Changes take effect on the next message sent in AI Sidekick.
          </Typography.Text>
        </Space>
      </Card>
    </>
  );
}
